import React, { useMemo, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { DollarSignIcon, ArrowRightIcon, CheckIcon } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { CookieBanner } from '../components/CookieBanner';
import { useLanguage } from '../contexts/LanguageContext';
import { getUTMDataForSubmission, appendUTMToUrl } from '../utils/utm';
const WORKER_URL = 'https://white-bonus-e94a.maxkhmlyt.workers.dev';
const COUNTRY_CODES = [{
  code: '+380',
  flag: '🇺🇦',
  country: 'Ukraine'
}, {
  code: '+375',
  flag: '🇧🇾',
  country: 'Belarus'
}, {
  code: '+372',
  flag: '🇪🇪',
  country: 'Estonia'
}, {
  code: '+371',
  flag: '🇱🇻',
  country: 'Latvia'
}, {
  code: '+370',
  flag: '🇱🇹',
  country: 'Lithuania'
}, {
  code: '+421',
  flag: '🇸🇰',
  country: 'Slovakia'
}, {
  code: '+420',
  flag: '🇨🇿',
  country: 'Czech'
}, {
  code: '+386',
  flag: '🇸🇮',
  country: 'Slovenia'
}, {
  code: '+385',
  flag: '🇭🇷',
  country: 'Croatia'
}, {
  code: '+381',
  flag: '🇷🇸',
  country: 'Serbia'
}, {
  code: '+359',
  flag: '🇧🇬',
  country: 'Bulgaria'
}, {
  code: '+358',
  flag: '🇫🇮',
  country: 'Finland'
}, {
  code: '+357',
  flag: '🇨🇾',
  country: 'Cyprus'
}, {
  code: '+356',
  flag: '🇲🇹',
  country: 'Malta'
}, {
  code: '+353',
  flag: '🇮🇪',
  country: 'Ireland'
}, {
  code: '+352',
  flag: '🇱🇺',
  country: 'Luxembourg'
}, {
  code: '+351',
  flag: '🇵🇹',
  country: 'Portugal'
}, {
  code: '+350',
  flag: '🇬🇮',
  country: 'Gibraltar'
}, {
  code: '+972',
  flag: '🇮🇱',
  country: 'Israel'
}, {
  code: '+971',
  flag: '🇦🇪',
  country: 'UAE'
}, {
  code: '+966',
  flag: '🇸🇦',
  country: 'Saudi Arabia'
}, {
  code: '+994',
  flag: '🇦🇿',
  country: 'Azerbaijan'
}, {
  code: '+995',
  flag: '🇬🇪',
  country: 'Georgia'
}, {
  code: '+998',
  flag: '🇺🇿',
  country: 'Uzbekistan'
}, {
  code: '+48',
  flag: '🇵🇱',
  country: 'Poland'
}, {
  code: '+49',
  flag: '🇩🇪',
  country: 'Germany'
}, {
  code: '+44',
  flag: '🇬🇧',
  country: 'UK'
}, {
  code: '+43',
  flag: '🇦🇹',
  country: 'Austria'
}, {
  code: '+41',
  flag: '🇨🇭',
  country: 'Switzerland'
}, {
  code: '+40',
  flag: '🇷🇴',
  country: 'Romania'
}, {
  code: '+39',
  flag: '🇮🇹',
  country: 'Italy'
}, {
  code: '+36',
  flag: '🇭🇺',
  country: 'Hungary'
}, {
  code: '+34',
  flag: '🇪🇸',
  country: 'Spain'
}, {
  code: '+33',
  flag: '🇫🇷',
  country: 'France'
}, {
  code: '+32',
  flag: '🇧🇪',
  country: 'Belgium'
}, {
  code: '+31',
  flag: '🇳🇱',
  country: 'Netherlands'
}, {
  code: '+30',
  flag: '🇬🇷',
  country: 'Greece'
}, {
  code: '+47',
  flag: '🇳🇴',
  country: 'Norway'
}, {
  code: '+46',
  flag: '🇸🇪',
  country: 'Sweden'
}, {
  code: '+45',
  flag: '🇩🇰',
  country: 'Denmark'
}, {
  code: '+90',
  flag: '🇹🇷',
  country: 'Turkey'
}, {
  code: '+1',
  flag: '🇺🇸',
  country: 'USA/Canada'
}, {
  code: '+7',
  flag: '🇷🇺',
  country: 'Russia/Kazakhstan'
}, {
  code: '+61',
  flag: '🇦🇺',
  country: 'Australia'
}, {
  code: '+81',
  flag: '🇯🇵',
  country: 'Japan'
}, {
  code: '+82',
  flag: '🇰🇷',
  country: 'South Korea'
}, {
  code: '+86',
  flag: '🇨🇳',
  country: 'China'
}, {
  code: '+91',
  flag: '🇮🇳',
  country: 'India'
}, {
  code: '+55',
  flag: '🇧🇷',
  country: 'Brazil'
}, {
  code: '+52',
  flag: '🇲🇽',
  country: 'Mexico'
}].sort((a, b) => b.code.length - a.code.length);
export function ReferralPage() {
  const heroRef = useRef(null);
  const heroInView = useInView(heroRef, {
    once: true,
    margin: '-100px'
  });
  const {
    language,
    getPath
  } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const languageNames: Record<string, string> = {
    en: 'English',
    ru: 'Русский',
    ua: 'Українська'
  };
  const consentText: Record<string, string> = {
    en: 'I agree to the processing of my personal data',
    ru: 'Я согласен на обработку моих персональных данных',
    ua: 'Я погоджуюсь на обробку моїх персональних даних'
  };
  const consentErrorText: Record<string, string> = {
    en: 'Please agree to continue',
    ru: 'Пожалуйста, дайте согласие для продолжения',
    ua: 'Будь ласка, дайте згоду для продовження'
  };
  const detectedCountry = useMemo(() => {
    const phone = formData.phone.replace(/\s/g, '');
    if (!phone.startsWith('+')) return null;
    for (const country of COUNTRY_CODES) {
      if (phone.startsWith(country.code)) {
        return country;
      }
    }
    return null;
  }, [formData.phone]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleConsentChange = () => {
    setConsent(!consent);
    if (!consent) {
      setConsentError(false);
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!consent) {
      setConsentError(true);
      return;
    }
    setIsSubmitting(true);
    const utmData = getUTMDataForSubmission();
    try {
      await fetch(WORKER_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message || 'Не указано',
          form_type: 'Реферальная программа',
          page_url: window.location.href,
          language: languageNames[language] || language,
          ...utmData
        })
      });
    } catch {
      // Ignore errors
    }
    const thankYouPath = getPath('thank-you-referral');
    window.location.href = appendUTMToUrl(thankYouPath);
  };
  return <div className="min-h-screen" style={{
    backgroundColor: 'var(--bg-primary)'
  }} data-id="element-384">
      <Navigation data-id="element-385" />

      {/* Hero Section */}
      <section ref={heroRef} className="relative pt-32 pb-20 px-4 overflow-hidden" data-id="element-386">
        <div className="absolute inset-0 overflow-hidden opacity-20" data-id="element-387">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full blur-3xl" style={{
          background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)'
        }} data-id="element-388" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto" data-id="element-389">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={heroInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} className="text-center mb-16" data-id="element-390">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight" style={{
            color: 'var(--text-primary)'
          }} data-id="element-391">
              Referral{' '}
              <span style={{
              color: 'var(--accent)'
            }} data-id="element-392">
                Program
              </span>
            </h1>
            <p className="text-lg sm:text-xl max-w-2xl mx-auto" style={{
            color: 'var(--text-secondary)'
          }} data-id="element-393">
              Earn €100 for every successful referral
            </p>
          </motion.div>

          <div className="grid gap-8 lg:grid-cols-2 lg:gap-12 max-w-6xl mx-auto items-stretch" data-id="element-394">
            {/* Left - Information */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={heroInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="order-2 lg:order-1" data-id="element-395">
              <div className="rounded-2xl border p-8 h-full flex flex-col" style={{
              borderColor: 'var(--border)',
              backgroundColor: 'var(--bg-secondary)'
            }} data-id="element-396">
                <div className="flex items-center gap-4 mb-8" data-id="element-397">
                  <div className="p-4 rounded-xl" style={{
                  backgroundColor: 'var(--accent)'
                }} data-id="element-398">
                    <DollarSignIcon className="w-8 h-8 text-white" data-id="element-399" />
                  </div>
                  <div data-id="element-400">
                    <h2 className="text-3xl font-bold" style={{
                    color: 'var(--accent)'
                  }} data-id="element-401">
                      €100
                    </h2>
                    <p className="text-sm" style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-402">
                      per successful referral
                    </p>
                  </div>
                </div>

                <div className="space-y-6 flex-1" data-id="element-403">
                  <div data-id="element-404">
                    <h3 className="text-xl font-bold mb-3" style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-405">
                      How It Works
                    </h3>
                    <div className="space-y-4" data-id="element-406">
                      <div className="flex gap-4" data-id="element-407">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" style={{
                        backgroundColor: 'var(--accent)',
                        color: '#FFFFFF'
                      }} data-id="element-408">
                          1
                        </div>
                        <div data-id="element-409">
                          <h4 className="font-medium mb-1" style={{
                          color: 'var(--text-primary)'
                        }} data-id="element-410">
                            Recommend Us
                          </h4>
                          <p className="text-sm" style={{
                          color: 'var(--text-secondary)'
                        }} data-id="element-411">
                            Share IT Monsters with friends, colleagues, or your
                            network who need a website.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-4" data-id="element-412">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" style={{
                        backgroundColor: 'var(--accent)',
                        color: '#FFFFFF'
                      }} data-id="element-413">
                          2
                        </div>
                        <div data-id="element-414">
                          <h4 className="font-medium mb-1" style={{
                          color: 'var(--text-primary)'
                        }} data-id="element-415">
                            They Get Demo
                          </h4>
                          <p className="text-sm" style={{
                          color: 'var(--text-secondary)'
                        }} data-id="element-416">
                            Your referral receives a free website demo. If they
                            proceed with payment, you earn.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-4" data-id="element-417">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" style={{
                        backgroundColor: 'var(--accent)',
                        color: '#FFFFFF'
                      }} data-id="element-418">
                          3
                        </div>
                        <div data-id="element-419">
                          <h4 className="font-medium mb-1" style={{
                          color: 'var(--text-primary)'
                        }} data-id="element-420">
                            You Earn €100
                          </h4>
                          <p className="text-sm" style={{
                          color: 'var(--text-secondary)'
                        }} data-id="element-421">
                            Receive €100 paid directly to you within 7 days
                            after successful payment.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="pt-6 border-t" style={{
                  borderColor: 'var(--border)'
                }} data-id="element-422">
                    <h3 className="text-lg font-bold mb-3" style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-423">
                      Why Join?
                    </h3>
                    <ul className="space-y-2 text-sm" style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-424">
                      <li className="flex items-center gap-2" data-id="element-425">
                        <span style={{
                        color: 'var(--accent)'
                      }} data-id="element-426">
                          ✓
                        </span>
                        €100 per referral
                      </li>
                      <li className="flex items-center gap-2" data-id="element-427">
                        <span style={{
                        color: 'var(--accent)'
                      }} data-id="element-428">
                          ✓
                        </span>
                        No limits on earnings
                      </li>
                      <li className="flex items-center gap-2" data-id="element-429">
                        <span style={{
                        color: 'var(--accent)'
                      }} data-id="element-430">
                          ✓
                        </span>
                        Easy to sell (free demo)
                      </li>
                      <li className="flex items-center gap-2" data-id="element-431">
                        <span style={{
                        color: 'var(--accent)'
                      }} data-id="element-432">
                          ✓
                        </span>
                        Fast payouts (7 days)
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right - Referral Form */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} animate={heroInView ? {
            opacity: 1,
            x: 0
          } : {}} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="order-1 lg:order-2" data-id="element-433">
              <div className="rounded-2xl border p-8 h-full flex flex-col" style={{
              borderColor: 'var(--border)',
              backgroundColor: 'var(--bg-secondary)'
            }} data-id="element-434">
                <h2 className="text-2xl font-bold mb-2" style={{
                color: 'var(--text-primary)'
              }} data-id="element-435">
                  Join Referral Program
                </h2>
                <p className="text-sm mb-6" style={{
                color: 'var(--text-secondary)'
              }} data-id="element-436">
                  Fill out the form below and we'll contact you with your unique
                  referral link.
                </p>

                <form onSubmit={handleSubmit} className="space-y-5 flex-1 flex flex-col" data-id="element-437">
                  <div data-id="element-438">
                    <label htmlFor="ref-name" className="mb-2 block text-sm font-medium" style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-439">
                      Your Name *
                    </label>
                    <input type="text" id="ref-name" name="name" value={formData.name} onChange={handleChange} required className="w-full rounded-xl border px-4 py-3 text-sm transition-all focus:outline-none focus:ring-2" style={{
                    borderColor: 'var(--border)',
                    backgroundColor: 'var(--bg-tertiary)',
                    color: 'var(--text-primary)'
                  }} placeholder="John Doe" data-id="element-440" />
                  </div>

                  <div data-id="element-441">
                    <label htmlFor="ref-email" className="mb-2 block text-sm font-medium" style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-442">
                      Email Address *
                    </label>
                    <input type="email" id="ref-email" name="email" value={formData.email} onChange={handleChange} required className="w-full rounded-xl border px-4 py-3 text-sm transition-all focus:outline-none focus:ring-2" style={{
                    borderColor: 'var(--border)',
                    backgroundColor: 'var(--bg-tertiary)',
                    color: 'var(--text-primary)'
                  }} placeholder="john@example.com" data-id="element-443" />
                  </div>

                  <div data-id="element-444">
                    <label htmlFor="ref-phone" className="mb-2 block text-sm font-medium" style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-445">
                      Phone *
                    </label>
                    <div className="relative" data-id="element-446">
                      {detectedCountry && <div className="absolute left-3 top-1/2 -translate-y-1/2 text-xl pointer-events-none" title={detectedCountry.country} data-id="element-447">
                          {detectedCountry.flag}
                        </div>}
                      <input type="tel" id="ref-phone" name="phone" value={formData.phone} onChange={handleChange} required className="w-full rounded-xl border px-4 py-3 text-sm transition-all focus:outline-none focus:ring-2" style={{
                      borderColor: 'var(--border)',
                      backgroundColor: 'var(--bg-tertiary)',
                      color: 'var(--text-primary)',
                      paddingLeft: detectedCountry ? '2.75rem' : '1rem'
                    }} placeholder="+1 234 567 8900" data-id="element-448" />
                    </div>
                  </div>

                  <div className="flex-1" data-id="element-449">
                    <label htmlFor="ref-message" className="mb-2 block text-sm font-medium" style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-450">
                      Tell Us About Your Network
                    </label>
                    <textarea id="ref-message" name="message" value={formData.message} onChange={handleChange} rows={3} className="w-full resize-none rounded-xl border px-4 py-3 text-sm transition-all focus:outline-none focus:ring-2" style={{
                    borderColor: 'var(--border)',
                    backgroundColor: 'var(--bg-tertiary)',
                    color: 'var(--text-primary)'
                  }} placeholder="Who do you plan to refer? (e.g., small business owners, entrepreneurs, etc.)" data-id="element-451" />
                  </div>

                  {/* Consent Checkbox */}
                  <motion.div className={`flex items-start gap-3 p-3 rounded-xl transition-all ${consentError ? 'bg-red-500/10' : ''}`} animate={consentError ? {
                  x: [0, -5, 5, -5, 5, 0]
                } : {}} transition={{
                  duration: 0.4
                }} data-id="element-452">
                    <button type="button" onClick={handleConsentChange} className="flex-shrink-0 w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center transition-all" style={{
                    borderColor: consentError ? '#ef4444' : consent ? 'var(--accent)' : 'var(--border)',
                    backgroundColor: consent ? 'var(--accent)' : 'transparent'
                  }} data-id="element-453">
                      {consent && <CheckIcon className="w-3 h-3 text-white" data-id="element-454" />}
                    </button>
                    <div className="flex-1" data-id="element-455">
                      <label className="text-sm cursor-pointer" style={{
                      color: consentError ? '#ef4444' : 'var(--text-secondary)'
                    }} onClick={handleConsentChange} data-id="element-456">
                        {consentText[language] || consentText.en} *
                      </label>
                      {consentError && <motion.p initial={{
                      opacity: 0,
                      y: -5
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} className="text-xs mt-1 font-medium" style={{
                      color: '#ef4444'
                    }} data-id="element-457">
                          {consentErrorText[language] || consentErrorText.en}
                        </motion.p>}
                    </div>
                  </motion.div>

                  <motion.button type="submit" disabled={isSubmitting} whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} className="w-full rounded-xl py-4 font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-70" style={{
                  backgroundColor: 'var(--accent)',
                  color: '#FFFFFF'
                }} data-id="element-458">
                    <span className="flex items-center justify-center gap-2" data-id="element-459">
                      {isSubmitting ? 'Submitting...' : <>
                          Join Program
                          <ArrowRightIcon className="h-5 w-5" data-id="element-460" />
                        </>}
                    </span>
                  </motion.button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer data-id="element-461" />
      <CookieBanner data-id="element-462" />
    </div>;
}